import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import AmbientParticles from "@/components/site/AmbientParticles";

/**
 * Privacy Policy — /privacy
 *
 * Legal/static page. Follows the existing site layout convention (Navbar +
 * dark obsidian bg + AmbientParticles + Footer). Long-form typography uses the
 * platinum body scale from the rest of the site — no new patterns invented.
 *
 * Content pending resolutions (per the build brief):
 *   • Entity registration status — currently reads "a brand operated by Esthera
 *     Ventures Global, based in India" per Nishq's decision on 2026-08-15
 *     (pending legal counsel finalising the formal entity structure). Keep this
 *     in lockstep with the openingLine in TermsPage.jsx.
 *   • Analytics/cookies disclosure — reflects PostHog (currently live in
 *     public/index.html). If PostHog is removed, update the analyticsLine
 *     constant below.
 *   • India DPDPA / UAE PDPL alignment — worded as "intended to align",
 *     verbatim from the brief; do not strengthen without legal review.
 */

const LAST_UPDATED = "November 2026";

const entityLine =
  'EstheraRealityOS is a brand operated by Esthera Ventures Global, based in ' +
  'India. This Privacy Policy explains what information we collect through ' +
  'estherarealityos.com, how we use it, and the choices you have.';

const analyticsLine =
  'We use PostHog to understand aggregate site traffic and usage patterns ' +
  '(page views, navigation flows, and general engagement) so we can improve ' +
  'the site. PostHog does not use advertising cookies, and we do not use ' +
  'this data to build individual advertising profiles.';

function Section({ n, title, children }) {
  return (
    <section className="mt-12 fade-up">
      <div className="flex items-baseline gap-3">
        <span className="text-[11px] tracking-[0.32em] uppercase text-sapphire-glow/85 font-semibold">
          {String(n).padStart(2, "0")}
        </span>
        <h2 className="font-display text-platinum text-[22px] sm:text-[26px] leading-[1.2] tracking-[-0.012em]">
          {title}
        </h2>
      </div>
      <div className="mt-4 text-[14.5px] text-platinum/72 leading-[1.75] space-y-4 max-w-[680px]">
        {children}
      </div>
    </section>
  );
}

export default function PrivacyPolicyPage() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="privacy-page">
      <AmbientParticles />
      <Navbar />

      {/* Hero band — matches other secondary pages */}
      <section className="relative pt-32 pb-10">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <div className="fade-up inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10.5px] tracking-[0.24em] uppercase text-sapphire-glow font-semibold"
            style={{ background: "rgba(37,99,235,0.08)", border: "1px solid rgba(96,165,250,0.28)" }}>
            <ShieldCheck className="w-3 h-3" />
            Legal
          </div>
          <h1 className="mt-5 font-display text-platinum text-[36px] sm:text-[44px] lg:text-[54px] leading-[1.05] tracking-[-0.02em] fade-up"
            style={{ animationDelay: "60ms" }}>
            Privacy <span className="text-sapphire-glow" style={{ textShadow: "0 0 30px rgba(96,165,250,0.35)" }}>Policy</span>
          </h1>
          <p className="mt-3 text-[12.5px] tracking-[0.22em] uppercase text-platinum/45 fade-up" style={{ animationDelay: "120ms" }}>
            Last updated · {LAST_UPDATED}
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="relative pb-24">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <div className="rounded-2xl border border-white/[0.06] p-6 sm:p-10 lg:p-14 fade-up"
            style={{ background: "linear-gradient(180deg, rgba(15,20,36,0.55) 0%, rgba(10,15,26,0.85) 100%)" }}>

            <p className="text-[14.5px] text-platinum/78 leading-[1.75] max-w-[680px]">
              {entityLine}
            </p>

            <Section n={1} title="Information We Collect">
              <div>
                <div className="text-platinum/85 font-medium">Information you provide directly:</div>
                <p className="mt-1.5">
                  When you request an Operations Audit or fill out our contact form, we collect the
                  information you submit — for example, your name, organization, work email,
                  organization type, market segment, and details about your current lead-handling
                  operations (response times, CRM usage, lead volume, team size, and similar
                  operational details).
                </p>
                <p>If you contact us by email, we retain that correspondence.</p>
              </div>
              <div>
                <div className="text-platinum/85 font-medium">Information collected automatically:</div>
                <p className="mt-1.5">{analyticsLine}</p>
              </div>
              <p>We do not knowingly collect information from anyone under the age of 18.</p>
            </Section>

            <Section n={2} title="How We Use Your Information">
              <p>We use the information you provide to:</p>
              <ul className="list-disc pl-5 space-y-2 marker:text-sapphire-glow/60">
                <li>Generate and deliver the Operations Audit or other materials you&rsquo;ve requested</li>
                <li>Respond to your inquiries and follow up on services you&rsquo;ve expressed interest in</li>
                <li>Improve the accuracy and relevance of our audits and proposals</li>
                <li>Communicate with you about Esthera&rsquo;s services</li>
              </ul>
              <p>
                We do not use your information for any purpose beyond operating the services you&rsquo;ve
                engaged with us for, and we do not sell your personal information to third parties.
              </p>
            </Section>

            <Section n={3} title="How We Store and Process Your Information">
              <p>
                We store the information you provide using <span className="text-platinum">Airtable</span> (our
                operational database) and <span className="text-platinum">Google Workspace</span> (for email
                correspondence). Where we use AI tools (such as OpenAI&rsquo;s API) to help generate your
                audit or proposal, your submitted information may be processed by that tool solely
                to produce your deliverable — it is not used to train third-party AI models, per
                OpenAI&rsquo;s standard API data-usage terms.
              </p>
              <p>
                Because our tools (Airtable, OpenAI, Google Workspace) may process data on servers
                located outside India — including in the United States or European Union — your
                information may be transferred internationally as part of normal service delivery.
                We take reasonable steps to ensure these providers handle data securely.
              </p>
            </Section>

            <Section n={4} title="Who We Share Your Information With">
              <p>We do not sell, rent, or trade your personal information. We may share information with:</p>
              <ul className="list-disc pl-5 space-y-2 marker:text-sapphire-glow/60">
                <li>Service providers who help us operate (e.g. Airtable, Google Workspace, OpenAI) — solely to the extent needed to deliver our services</li>
                <li>Legal authorities, if required by law</li>
              </ul>
              <p>We restrict access to your information to the people at Esthera directly involved in your engagement.</p>
            </Section>

            <Section n={5} title="Data Retention and Your Rights">
              <p>
                We retain your information for as long as needed to provide the services
                you&rsquo;ve requested, and for a reasonable period afterward for record-keeping.
                You may request that we:
              </p>
              <ul className="list-disc pl-5 space-y-2 marker:text-sapphire-glow/60">
                <li>Tell you what information we hold about you</li>
                <li>Correct inaccurate information</li>
                <li>Delete your information, or return it to you</li>
              </ul>
              <p>
                To make any of these requests, email us at{" "}
                <a href="mailto:privacy@estherarealityos.com" className="text-sapphire-glow hover:text-white transition-colors underline decoration-dotted underline-offset-4">
                  privacy@estherarealityos.com
                </a>. We will respond within a reasonable time.
              </p>
              <p>
                If you are located in India, this Policy is intended to align with the
                Digital Personal Data Protection Act, 2023. If you are located in the UAE,
                we aim to align with the UAE Personal Data Protection Law where applicable.
              </p>
            </Section>

            <Section n={6} title="Security">
              <p>
                We take reasonable measures to protect your information, including restricting
                access to your data to those directly involved in your engagement. No method
                of transmission or storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </Section>

            <Section n={7} title="Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. The &ldquo;Last Updated&rdquo; date at
                the top reflects the most recent version. Material changes will be reflected here.
              </p>
            </Section>

            <Section n={8} title="Contact Us">
              <p>If you have questions about this Privacy Policy or how we handle your information, contact us at:</p>
              <p>
                <a href="mailto:privacy@estherarealityos.com" className="text-sapphire-glow hover:text-white transition-colors">
                  privacy@estherarealityos.com
                </a>
              </p>
            </Section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
